import {
	EXCLUSION_REASON_PARAMS,
	isReasonCountableInBucket,
	NO_REASON_FILTER_PARAM,
} from "@signalist/shared-types";

import { InclusionFilterStatus } from "@/enum/list.enum";

/**
 * Taxonomy of the exclusion-reason `filterParam`s surfaced in the lead list (PREC-3536
 * Phase 5): which reason is recoverable via "Enrich anyway" and how each (bucket, param)
 * pair is labelled in the grouped Show filter and the Reason column.
 */

/** The recoverable reason written by the title gate — drives "Enrich anyway" vs plain Retry. */
export const RECOVERABLE_FILTER_PARAM = "jobTitle";

export const isRecoverableReason = (filterParam?: string | null): boolean =>
	filterParam === RECOVERABLE_FILTER_PARAM;

/** Coerce a lean-count value: SQL aggregates arrive as strings at runtime (web rule §6). */
export const toCount = (value?: number | null): number => Number(value ?? 0) || 0;

/**
 * The reasons that explain the bucket the lead is actually in (PREC-4083).
 *
 * Blacklisting a lead overwrites `inclusionStatus` and leaves its earlier reasons on the row, so a
 * blacklisted lead still carries "Job title excluded" or "No contact details found". The API counts
 * only the reasons that match the bucket (`buildReasonBucketScopeSql`), and the Reason column has
 * to apply the same rule or the table contradicts the Show filter it was filtered with.
 *
 * PREC-4094 gave `inOtherList` the same treatment, for the same reason: a lead added by hand from
 * Compose is refused, records that reason, and is then force-set back to ADDED — so without the
 * rule the Found bucket would break down by "Already on another list".
 */
export const visibleExclusionReasons = <T extends { filterParam?: string | null }>(row: {
	inclusionStatus?: string | null;
	exclusionReasons?: T[] | null;
}): T[] => {
	return (row.exclusionReasons ?? []).filter((reason) =>
		isReasonCountableInBucket(reason.filterParam, row.inclusionStatus)
	);
};

/**
 * Labels that depend on the bucket as well as the param; bucket-scoped keys win over param-scoped
 * ones. `company` used to be one: it backed "Never worked at the company" in NOT_FOUND until the
 * wrong-company verdict got its own param (2026-09-02 rebucket), and since then every "company"
 * reason means "Does not work there anymore", in every bucket (PREC-4519: 2,129 of them in Found
 * read "Wrong company", the old param default; PREC-4083 had the same under Blacklist).
 */
const BUCKET_LABEL_KEYS: Record<string, string> = {
	// A lead blacklisted before the verdict was recorded: "Other" reads as a leftover, and in this
	// bucket the honest statement is that the reason was never written down.
	[`${InclusionFilterStatus.BLACKLIST}_${NO_REASON_FILTER_PARAM}`]:
		"reasonLabels.blacklistNoReason",
};

/**
 * Every fixed `filterParam` needs an entry here, keyed off the shared vocabulary rather than
 * hand-written strings (PREC-3871). `contactInfo` was missing, so the dropdown fell through to
 * `ts(filterParam)` and rendered the raw param — "ContactInfo" — beside properly translated
 * siblings. The spec asserts this map covers `EXCLUSION_REASON_PARAMS`, so the next param added
 * fails a test instead of quietly leaking its identifier into the UI.
 */
const PARAM_LABEL_KEYS: Record<string, string> = {
	[EXCLUSION_REASON_PARAMS.jobTitle]: "reasonLabels.jobTitleExcluded",
	[EXCLUSION_REASON_PARAMS.person]: "reasonLabels.personNotFound",
	// PREC-4519 / PREC-4520: once a failed search has its own reason (processingError), "No contact
	// details found" and "Not found on LinkedIn or email" both mean "we searched and found nobody",
	// so they read as one label (and are one child of the Show filter, REASON_PARAM_ALIASES).
	[EXCLUSION_REASON_PARAMS.contactInfo]: "reasonLabels.personNotFound",
	[EXCLUSION_REASON_PARAMS.company]: "reasonLabels.noLongerThere",
	// Its own param since the verdict moved to Unsure; the bare "company" there already means
	// "Doesn't work there anymore" (list 6796).
	[EXCLUSION_REASON_PARAMS.wrongCompany]: "reasonLabels.neverWorkedHere",
	[EXCLUSION_REASON_PARAMS.title]: "reasonLabels.titleSearchMismatch",
	[EXCLUSION_REASON_PARAMS.country]: "reasonLabels.countrySearchMismatch",
	[EXCLUSION_REASON_PARAMS.companyNotFound]: "reasonLabels.companyNotFound",
	[EXCLUSION_REASON_PARAMS.personNotIdentified]: "reasonLabels.personNotIdentified",
	// PREC-3899: every candidate Company Enrich offered was already in Contacts.
	[EXCLUSION_REASON_PARAMS.noNewPerson]: "reasonLabels.noNewPerson",
	[EXCLUSION_REASON_PARAMS.icpCompanyMismatch]: "reasonLabels.icpCompanyMismatch",
	[EXCLUSION_REASON_PARAMS.processingError]: "reasonLabels.processingError",
	// PREC-4520: ours, but the row's own data cannot be stored, so no Retry is offered.
	[EXCLUSION_REASON_PARAMS.processingErrorPermanent]: "reasonLabels.processingErrorPermanent",
	// PREC-4522: the LinkedIn profile could not be read, so the employer was never checked.
	[EXCLUSION_REASON_PARAMS.employerUnverified]: "reasonLabels.employerUnverified",
	// PREC-4283: an AI company comparison returned an uncertain confidence score (50..74).
	[EXCLUSION_REASON_PARAMS.companyMatchUncertain]: "reasonLabels.companyMatchUncertain",
	// PREC-4283: matched by name only, and the name is not unique in the customer's lists.
	[EXCLUSION_REASON_PARAMS.companyNameAmbiguous]: "reasonLabels.companyNameAmbiguous",
	// PREC-4283: an employer known by name only that none of the customer's lists carries.
	[EXCLUSION_REASON_PARAMS.companyNotIdentified]: "reasonLabels.companyNotIdentified",
	// PREC-4622: a list filter could not be read; the reason text names the filter.
	[EXCLUSION_REASON_PARAMS.unreadableFilter]: "reasonLabels.unreadableFilter",
	// The blacklist verdicts (PREC-4083) — countable only inside the Blacklist bucket.
	[EXCLUSION_REASON_PARAMS.competitor]: "reasonLabels.competitor",
	[EXCLUSION_REASON_PARAMS.doNotContact]: "reasonLabels.doNotContact",
	[EXCLUSION_REASON_PARAMS.unsubscribed]: "reasonLabels.unsubscribed",
	// PREC-4094: the only reason a Skipped lead carries. Its param used to be the free-text
	// "In another list", which fell through to `ts(filterParam)` and rendered the raw string.
	[EXCLUSION_REASON_PARAMS.inOtherList]: "reasonLabels.inOtherList",
	// PREC-4172: the bulk bar's "Exclude from list" — the reason text carries the user's name.
	[EXCLUSION_REASON_PARAMS.manuallyExcluded]: "reasonLabels.manuallyExcluded",
	// PREC-2548: the override modal took the lead off the blacklist — explains a lead in Found.
	[EXCLUSION_REASON_PARAMS.includedByUser]: "reasonLabels.includedByUser",
	[NO_REASON_FILTER_PARAM]: "reasonLabels.noReason",
};

/**
 * Reasons that are one and the same to the user, by the param that stands for them (PREC-4520):
 * the Show filter lists them as one child, and selecting it filters by all of them.
 */
const REASON_PARAM_ALIASES: Record<string, string> = {
	[EXCLUSION_REASON_PARAMS.contactInfo]: EXCLUSION_REASON_PARAMS.person,
};

/** The param a reason is listed under in the Show filter. */
export const canonicalReasonParam = (filterParam: string): string =>
	REASON_PARAM_ALIASES[filterParam] ?? filterParam;

/** Every param a Show filter child stands for: itself and the reasons listed under it. */
export const reasonParamsOf = (filterParam: string): string[] => [
	filterParam,
	...Object.keys(REASON_PARAM_ALIASES).filter(
		(alias) => REASON_PARAM_ALIASES[alias] === filterParam
	),
];

/**
 * Label for a reason sub-row. Unknown params (e.g. ICP filter params like
 * "prospect.seniority") fall back to the translated column name, mirroring the Reason
 * column's tooltip rendering.
 */
export const getReasonLabel = (
	ts: (key: string) => string,
	inclusionStatus: string,
	filterParam: string
): string => {
	const key =
		BUCKET_LABEL_KEYS[`${inclusionStatus}_${filterParam}`] ?? PARAM_LABEL_KEYS[filterParam];

	return key ? ts(key) : ts(filterParam.replace("prospect.", "").replace("organization.", ""));
};
